import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import ShareCheckinCard from '../components/ShareCheckinCard';
import SEO from '../components/SEO';
import {
  MapPin,
  Camera,
  NavigationArrow,
  SpinnerGap,
  Check,
  X,
  Plus,
  Star,
  Image,
  Trash,
  ForkKnife,
  MagnifyingGlass,
  CaretRight,
  Lightning,
  MapPinLine,
  Clock,
  Phone,
  Globe,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_TYPES = ['breakfast', 'brunch', 'lunch', 'dinner', 'drinks', 'coffee'];

const PHOTO_LABELS = [
  { value: 'food', label: 'Food' },
  { value: 'menu', label: 'Menu' },
  { value: 'interior', label: 'Interior' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'drinks', label: 'Drinks' },
  { value: 'receipt', label: 'Receipt' },
];

const CheckIn = () => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Steps: location -> pick_restaurant -> details -> done
  const [step, setStep] = useState('location');
  const [locating, setLocating] = useState(false);
  const [coords, setCoords] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Nearby search
  const [searching, setSearching] = useState(false);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [ownNearby, setOwnNearby] = useState([]);
  const [manualSearch, setManualSearch] = useState('');

  // Selected restaurant
  const [selected, setSelected] = useState(null);

  // Photos
  const [photos, setPhotos] = useState([]); // { file, preview, label }

  // Details
  const [mealType, setMealType] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Get location on mount
  useEffect(() => {
    getLocation();
  }, []);

  const getLocation = useCallback(() => {
    setLocating(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        setLocating(false);
        fetchNearby(c);
      },
      (err) => {
        setLocationError(
          err.code === 1
            ? 'Location access denied. Please enable location in your browser settings.'
            : 'Could not determine your location. Please try again.'
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  const fetchNearby = async (c) => {
    setSearching(true);
    try {
      const res = await api.post('/checkin/nearby', {
        lat: c.lat,
        lng: c.lng,
        radius: 200,
      });
      setNearbyPlaces(res.data.places || []);
      setOwnNearby(res.data.own_restaurants || []);
      setStep('pick_restaurant');
    } catch (error) {
      console.error('Nearby search error:', error);
      toast.error('Failed to find nearby restaurants');
      setStep('pick_restaurant'); // Still allow manual entry
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPlace = (place) => {
    setSelected({
      restaurant_name: place.name,
      address: place.address,
      suburb: place.suburb,
      lat: place.lat,
      lng: place.lng,
      rating: place.rating,
      website: place.website,
      phone: place.phone,
      place_id: place.place_id,
      photo_url: place.photo_url,
      google_maps_url: place.google_maps_url,
      price_label: place.price_label,
      description: place.description,
      opening_hours: place.opening_hours,
    });
    setStep('details');
  };

  const handleSelectOwn = (r) => {
    setSelected({
      restaurant_id: r.id,
      restaurant_name: r.name,
      suburb: r.suburb,
      lat: r.lat,
      lng: r.lng,
    });
    setStep('details');
  };

  const handleManualSelect = () => {
    if (!manualSearch.trim()) return;
    setSelected({
      restaurant_name: manualSearch.trim(),
      lat: coords?.lat,
      lng: coords?.lng,
    });
    setStep('details');
  };

  const addPhotos = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const newPhotos = newFiles.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      label: 'food',
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const setPhotoLabel = (idx, label) => {
    setPhotos((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, label } : p))
    );
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);

    try {
      const formData = new FormData();

      const photoLabels = {};
      photos.forEach((p) => {
        formData.append('files', p.file);
        photoLabels[p.file.name] = p.label;
      });

      const checkinData = {
        ...selected,
        meal_type: mealType,
        notes: notes || undefined,
        visit_date: new Date().toISOString().split('T')[0],
        photo_labels: photoLabels,
      };

      formData.append('data', JSON.stringify(checkinData));

      const res = await api.post('/checkin', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(`Checked in at ${res.data.restaurant_name}!`);
      setStep('done');
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => photos.forEach((p) => URL.revokeObjectURL(p.preview));
  }, []);

  const filteredPlaces = manualSearch
    ? nearbyPlaces.filter((p) =>
        p.name.toLowerCase().includes(manualSearch.toLowerCase())
      )
    : nearbyPlaces;

  return (
    <div className="container-app py-6 pb-8 max-w-lg mx-auto" data-testid="checkin-page">
      <SEO title="Check In" description="Check in to a nearby Cape Town restaurant and capture your dining moment." path="/checkin" noindex />
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-[#1A1918]" data-testid="checkin-title">
          Check In
        </h1>
        <p className="text-[#7A7873] text-sm mt-1">
          {step === 'location' && 'Finding your location...'}
          {step === 'pick_restaurant' && 'Where are you dining?'}
          {step === 'details' && 'Add photos and details'}
          {step === 'done' && 'All done!'}
        </p>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-2 mb-8" data-testid="checkin-progress">
        {['location', 'pick_restaurant', 'details'].map((s, i) => (
          <div
            key={s}
            className={`h-1.5 rounded-full transition-all ${
              step === s ? 'w-8 bg-[#A94438]' :
              ['location', 'pick_restaurant', 'details'].indexOf(step) > i
                ? 'w-4 bg-[#4B5945]'
                : 'w-4 bg-[#EAE8E3]'
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1: Location */}
        {step === 'location' && (
          <motion.div
            key="location"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center py-12"
          >
            {locating || searching ? (
              <div>
                <div className="w-20 h-20 mx-auto rounded-full bg-[#A94438]/10 flex items-center justify-center mb-4">
                  <NavigationArrow weight="duotone" className="w-10 h-10 text-[#A94438] animate-pulse" />
                </div>
                <p className="text-[#7A7873]">
                  {locating ? 'Finding your location...' : 'Searching nearby restaurants...'}
                </p>
                <SpinnerGap className="w-6 h-6 animate-spin text-[#A94438] mx-auto mt-4" />
              </div>
            ) : locationError ? (
              <div>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <MapPin weight="duotone" className="w-10 h-10 text-red-400" />
                </div>
                <p className="text-[#7A7873] mb-4">{locationError}</p>
                <Button onClick={getLocation} className="btn-primary" data-testid="retry-location-btn">
                  Try Again
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setStep('pick_restaurant')}
                  className="ml-2"
                  data-testid="skip-location-btn"
                >
                  Enter manually
                </Button>
              </div>
            ) : null}
          </motion.div>
        )}

        {/* STEP 2: Pick Restaurant */}
        {step === 'pick_restaurant' && (
          <motion.div
            key="pick"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Search / manual entry */}
            <div className="relative mb-4">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7873]" />
              <Input
                placeholder="Search or type restaurant name..."
                value={manualSearch}
                onChange={(e) => setManualSearch(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="restaurant-search-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleManualSelect();
                }}
              />
            </div>

            {manualSearch.trim() && !filteredPlaces.some(
              (p) => p.name.toLowerCase() === manualSearch.trim().toLowerCase()
            ) && (
              <button
                onClick={handleManualSelect}
                className="w-full text-left p-4 rounded-xl border-2 border-dashed border-[#A94438]/30 hover:border-[#A94438] hover:bg-[#A94438]/5 transition-colors mb-4"
                data-testid="manual-entry-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#A94438]/10 flex items-center justify-center">
                    <Plus weight="bold" className="w-5 h-5 text-[#A94438]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1918]">Add "{manualSearch.trim()}"</p>
                    <p className="text-xs text-[#7A7873]">Create new restaurant</p>
                  </div>
                </div>
              </button>
            )}

            {/* Own restaurants nearby */}
            {ownNearby.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#7A7873] uppercase tracking-wider mb-2">Your Restaurants Nearby</p>
                {ownNearby.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleSelectOwn(r)}
                    className="w-full text-left p-3 rounded-xl border border-[#4B5945]/30 bg-[#D1E2C4]/10 hover:bg-[#D1E2C4]/30 transition-colors mb-2"
                    data-testid={`own-restaurant-${r.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ForkKnife weight="duotone" className="w-5 h-5 text-[#4B5945]" />
                        <div>
                          <p className="font-medium text-[#1A1918] text-sm">{r.name}</p>
                          {r.suburb && <p className="text-xs text-[#7A7873]">{r.suburb}</p>}
                        </div>
                      </div>
                      {r.distance_m && (
                        <Badge variant="secondary" className="text-[10px]">{r.distance_m}m</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Nearby Places */}
            {filteredPlaces.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[#7A7873] uppercase tracking-wider mb-2">
                  {coords ? `Nearby Restaurants (${filteredPlaces.length})` : 'Results'}
                </p>
                <div className="space-y-2">
                  {filteredPlaces.map((place) => (
                    <button
                      key={place.place_id}
                      onClick={() => handleSelectPlace(place)}
                      className="w-full text-left rounded-xl border border-[#EAE8E3] hover:border-[#A94438]/30 hover:bg-[#F9F8F5] transition-colors overflow-hidden"
                      data-testid={`nearby-place-${place.place_id}`}
                    >
                      <div className="flex">
                        {/* Photo */}
                        {place.photo_url ? (
                          <img
                            src={place.photo_url}
                            alt={place.name}
                            className="w-20 h-full min-h-[80px] object-cover flex-shrink-0"
                            loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-20 min-h-[80px] bg-[#EAE8E3] flex items-center justify-center flex-shrink-0">
                            <MapPinLine weight="duotone" className="w-7 h-7 text-[#7A7873]" />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-[#1A1918] text-sm leading-tight truncate">{place.name}</p>
                            <CaretRight className="w-4 h-4 text-[#9A9590] flex-shrink-0 mt-0.5" />
                          </div>

                          {/* Rating + Price + Open status */}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {place.rating && (
                              <span className="flex items-center gap-0.5 text-xs text-[#C4A35A]">
                                <Star weight="fill" className="w-3 h-3" />
                                {place.rating}
                                {place.rating_count && (
                                  <span className="text-[#9A9590] text-[10px]">({place.rating_count})</span>
                                )}
                              </span>
                            )}
                            {place.price_label && (
                              <span className="text-[10px] text-[#7A7873] font-medium">{place.price_label}</span>
                            )}
                            {place.open_now != null && (
                              <span className={`text-[10px] font-medium ${place.open_now ? 'text-[#4B5945]' : 'text-[#A94438]'}`}>
                                {place.open_now ? 'Open now' : 'Closed'}
                              </span>
                            )}
                          </div>

                          {/* Address */}
                          <p className="text-[10px] text-[#9A9590] truncate mt-0.5">{place.address}</p>

                          {/* Quick info row */}
                          <div className="flex items-center gap-3 mt-1.5">
                            {place.phone && (
                              <span className="flex items-center gap-0.5 text-[10px] text-[#7A7873]">
                                <Phone weight="duotone" className="w-3 h-3" />
                                {place.phone}
                              </span>
                            )}
                            {place.website && (
                              <span className="flex items-center gap-0.5 text-[10px] text-[#7A7873]">
                                <Globe weight="duotone" className="w-3 h-3" />
                                Website
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!searching && filteredPlaces.length === 0 && ownNearby.length === 0 && !manualSearch && (
              <div className="text-center py-8">
                <MapPin weight="duotone" className="w-12 h-12 text-[#9A9590] mx-auto mb-3" />
                <p className="text-[#7A7873] text-sm">No restaurants found nearby.</p>
                <p className="text-[#9A9590] text-xs mt-1">Type a name above to add one.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* STEP 3: Details & Photos */}
        {step === 'details' && (
          <motion.div
            key="details"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {/* Selected restaurant header */}
            <div className="rounded-xl border border-[#EAE8E3] bg-white mb-6 overflow-hidden" data-testid="selected-restaurant">
              {selected?.photo_url && (
                <img
                  src={selected.photo_url}
                  alt={selected.restaurant_name}
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#A94438]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin weight="fill" className="w-5 h-5 text-[#A94438]" />
                    </div>
                    <div>
                      <p className="font-heading font-semibold text-[#1A1918]">{selected?.restaurant_name}</p>
                      {selected?.address && (
                        <p className="text-xs text-[#7A7873] truncate max-w-[220px]">{selected.address}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelected(null); setStep('pick_restaurant'); }}
                    className="text-[#7A7873] hover:text-[#A94438]"
                    data-testid="change-restaurant-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Quick metadata */}
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {selected?.rating && (
                    <span className="flex items-center gap-0.5 text-xs text-[#C4A35A]">
                      <Star weight="fill" className="w-3.5 h-3.5" />{selected.rating}
                    </span>
                  )}
                  {selected?.price_label && (
                    <span className="text-xs text-[#7A7873]">{selected.price_label}</span>
                  )}
                  {selected?.phone && (
                    <a href={`tel:${selected.phone}`} className="text-xs text-[#7A7873] flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Phone weight="duotone" className="w-3 h-3" />{selected.phone}
                    </a>
                  )}
                </div>

                {selected?.description && (
                  <p className="text-xs text-[#7A7873] mt-2 line-clamp-2">{selected.description}</p>
                )}

                {selected?.opening_hours?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-[#7A7873] cursor-pointer flex items-center gap-1 hover:text-[#1A1918]">
                      <Clock weight="duotone" className="w-3 h-3" /> Opening hours
                    </summary>
                    <div className="mt-1 space-y-0.5 pl-4">
                      {selected.opening_hours.map((h, i) => (
                        <p key={i} className="text-[10px] text-[#9A9590]">{h}</p>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>

            {/* Photos section */}
            <div className="mb-6">
              <p className="text-sm font-medium text-[#1A1918] mb-3">Photos</p>
              <div className="grid grid-cols-3 gap-3">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative group" data-testid={`photo-${idx}`}>
                    <img
                      src={photo.preview}
                      alt={`Photo ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-xl"
                    />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${idx}`}
                    >
                      <Trash weight="bold" className="w-3 h-3 text-white" />
                    </button>
                    <select
                      value={photo.label}
                      onChange={(e) => setPhotoLabel(idx, e.target.value)}
                      className="absolute bottom-1.5 left-1.5 right-1.5 text-[10px] bg-black/60 text-white rounded-lg px-1.5 py-1 border-0 appearance-auto"
                      data-testid={`photo-label-${idx}`}
                    >
                      {PHOTO_LABELS.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                ))}

                {/* Add photo button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#EAE8E3] hover:border-[#A94438]/40 flex flex-col items-center justify-center gap-1 transition-colors"
                  data-testid="add-photo-btn"
                >
                  <Camera weight="duotone" className="w-7 h-7 text-[#7A7873]" />
                  <span className="text-[10px] text-[#7A7873]">Add Photo</span>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={addPhotos}
                className="hidden"
                data-testid="photo-file-input"
              />
            </div>

            {/* Meal type */}
            <div className="mb-6">
              <p className="text-sm font-medium text-[#1A1918] mb-3">What meal?</p>
              <div className="flex flex-wrap gap-2" data-testid="meal-type-picker">
                {MEAL_TYPES.map((mt) => (
                  <button
                    key={mt}
                    onClick={() => setMealType(mealType === mt ? null : mt)}
                    className={`filter-pill ${mealType === mt ? 'active' : ''}`}
                    data-testid={`meal-type-${mt}`}
                  >
                    {mt.charAt(0).toUpperCase() + mt.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="mb-8">
              <p className="text-sm font-medium text-[#1A1918] mb-3">Notes (optional)</p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="How was it? What did you have?"
                rows={3}
                className="w-full rounded-xl border border-[#EAE8E3] bg-white px-4 py-3 text-sm text-[#1A1918] placeholder:text-[#9A9590] focus:outline-none focus:ring-2 focus:ring-[#A94438]/20 focus:border-[#A94438]/40 resize-none"
                data-testid="notes-input"
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full btn-primary text-lg py-6"
              data-testid="submit-checkin-btn"
            >
              {submitting ? (
                <SpinnerGap className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Lightning weight="fill" className="w-6 h-6 mr-2" />
                  Check In
                </>
              )}
            </Button>
          </motion.div>
        )}

        {/* STEP 4: Done + Share */}
        {step === 'done' && (
          <motion.div
            key="done"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-10"
          >
            <div className="w-20 h-20 mx-auto rounded-full bg-[#4B5945] flex items-center justify-center mb-4">
              <Check weight="bold" className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-heading font-semibold text-[#1A1918] mb-2">Checked In!</h2>
            <p className="text-[#7A7873] mb-2">
              at {selected?.restaurant_name}
            </p>
            {photos.length > 0 && (
              <p className="text-[#7A7873] text-sm mb-6">
                {photos.length} photo{photos.length !== 1 ? 's' : ''} saved
              </p>
            )}

            {/* Share card */}
            <div className="bg-white rounded-2xl border border-[#EAE8E3] p-5 mb-6 text-left">
              <p className="text-sm font-medium text-[#1A1918] mb-3">Share your check-in</p>

              {/* Preview */}
              <div className="rounded-xl bg-[#1A1918] p-4 mb-4 text-center">
                <p className="text-[#A94438] text-xs font-semibold tracking-wider mb-1">DININGTRACKER.CO.ZA</p>
                <p className="text-white/50 text-xs mb-1">I'm dining at</p>
                <p className="text-white font-heading font-bold text-lg">{selected?.restaurant_name}</p>
                {selected?.address && (
                  <p className="text-[#C4A35A] text-xs mt-1 truncate">{selected.address}</p>
                )}
                {photos.length > 0 && (
                  <div className="flex justify-center gap-1.5 mt-3">
                    {photos.slice(0, 4).map((p, i) => (
                      <img key={i} src={p.preview} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    ))}
                  </div>
                )}
              </div>

              <ShareCheckinCard
                restaurantName={selected?.restaurant_name}
                address={selected?.address}
                mealType={mealType}
                photos={photos}
                date={new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
              />
            </div>

            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#7A7873] hover:text-[#1A1918]"
              data-testid="done-go-home-btn"
            >
              Back to Dashboard
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CheckIn;
